import React, { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import logo from '../assets/Logo_laboratorio_simple_verde-removebg-preview.png';
import { useAuth } from '../context/AuthContext';
import UserMenu from '../components/UserMenu';
import TextInput from '../components/TextInput';
import Select from '../components/Select';
import Button from '../components/Button';
import ErrorBanner from '../components/ErrorBanner';
import Modal from '../components/Modal';
import { getMiCuenta, actualizarMiCuenta, listarHijos, vincularHijo, actualizarHijo } from '../api/authApi';
import { getObrasSociales, ObraSocialOption } from '../api/obraSocialApi';
import { homeForRole } from '../utils/roleHome';
import { ActualizarHijoRequest, ApiError, HijoResponse, MiCuentaInfo, VincularHijoRequest } from '../types/auth.types';
import styles from './MiCuentaPage.module.css';

const EMPTY_HIJO_FORM: VincularHijoRequest = {
  nombre: '',
  apellido: '',
  dni: '',
  sexo: 'M',
  fechaNacimiento: '',
  particular: false,
  nroAfiliado: '',
  obraSocial: '',
};

const EMPTY_HIJO_EDIT_FORM: ActualizarHijoRequest = {
  particular: true,
  nroAfiliado: '',
  obraSocial: '',
};

function calcularEdad(fechaNacimientoIso: string): number {
  const nacimiento = new Date(`${fechaNacimientoIso}T00:00:00`);
  const hoy = new Date();
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const aunNoCumplio =
    hoy.getMonth() < nacimiento.getMonth() ||
    (hoy.getMonth() === nacimiento.getMonth() && hoy.getDate() < nacimiento.getDate());
  if (aunNoCumplio) edad -= 1;
  return edad;
}

export default function MiCuentaPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [cuenta, setCuenta] = useState<MiCuentaInfo | null>(null);
  const [obrasSociales, setObrasSociales] = useState<ObraSocialOption[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState<'cuenta' | 'hijos'>('cuenta');

  const [email, setEmail] = useState('');
  const [particular, setParticular] = useState(true);
  const [obraSocial, setObraSocial] = useState('');
  const [nroAfiliado, setNroAfiliado] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [hijos, setHijos] = useState<HijoResponse[]>([]);
  const [loadingHijos, setLoadingHijos] = useState(false);
  const [hijoModalOpen, setHijoModalOpen] = useState(false);
  const [hijoForm, setHijoForm] = useState<VincularHijoRequest>(EMPTY_HIJO_FORM);
  const [hijoLoading, setHijoLoading] = useState(false);
  const [hijoError, setHijoError] = useState<string | null>(null);

  const [editingHijo, setEditingHijo] = useState<HijoResponse | null>(null);
  const [hijoEditForm, setHijoEditForm] = useState<ActualizarHijoRequest>(EMPTY_HIJO_EDIT_FORM);
  const [hijoEditLoading, setHijoEditLoading] = useState(false);
  const [hijoEditError, setHijoEditError] = useState<string | null>(null);

  const isViewer = user?.role === 'VIEWER';

  useEffect(() => {
    Promise.all([getMiCuenta(), getObrasSociales()])
      .then(([info, obras]) => {
        setCuenta(info);
        setEmail(info.email);
        setParticular(info.particular);
        setObraSocial(info.obraSocial ?? '');
        setNroAfiliado(info.nroAfiliado ?? '');
        setObrasSociales(obras);
      })
      .finally(() => setLoadingData(false));
  }, []);

  useEffect(() => {
    if (!isViewer) return;
    setLoadingHijos(true);
    listarHijos().then(setHijos).finally(() => setLoadingHijos(false));
  }, [isViewer]);

  function setHijoField(field: Partial<VincularHijoRequest>) {
    setHijoForm((prev) => ({ ...prev, ...field }));
  }

  function handleHijoParticular(checked: boolean) {
    setHijoField({ particular: checked, obraSocial: checked ? '' : hijoForm.obraSocial, nroAfiliado: checked ? '' : hijoForm.nroAfiliado });
  }

  function openVincularHijo() {
    setHijoForm(EMPTY_HIJO_FORM);
    setHijoError(null);
    setHijoModalOpen(true);
  }

  function closeVincularHijo() {
    setHijoModalOpen(false);
    setHijoError(null);
  }

  async function handleVincularHijo(e: FormEvent) {
    e.preventDefault();
    setHijoError(null);

    if (!hijoForm.nombre.trim()) { setHijoError('El nombre es obligatorio.'); return; }
    if (!hijoForm.apellido.trim()) { setHijoError('El apellido es obligatorio.'); return; }
    if (!/^\d{7,8}$/.test(hijoForm.dni.trim())) { setHijoError('El DNI debe tener 7 u 8 dígitos.'); return; }
    if (!hijoForm.fechaNacimiento) { setHijoError('La fecha de nacimiento es obligatoria.'); return; }
    if (calcularEdad(hijoForm.fechaNacimiento) >= 18) {
      setHijoError('Solo se pueden vincular hijos menores de 18 años.');
      return;
    }
    if (!hijoForm.particular) {
      if (!hijoForm.obraSocial.trim()) { setHijoError('La obra social es obligatoria para pacientes no particulares.'); return; }
      if (!hijoForm.nroAfiliado.trim()) { setHijoError('El número de afiliado es obligatorio para pacientes no particulares.'); return; }
    }

    setHijoLoading(true);
    try {
      const nuevo = await vincularHijo({
        ...hijoForm,
        nombre: hijoForm.nombre.trim(),
        apellido: hijoForm.apellido.trim(),
        dni: hijoForm.dni.trim(),
        obraSocial: hijoForm.particular ? '' : hijoForm.obraSocial.trim(),
        nroAfiliado: hijoForm.particular ? '' : hijoForm.nroAfiliado.trim(),
      });
      setHijos((prev) => [...prev, nuevo].sort((a, b) => a.apellido.localeCompare(b.apellido)));
      setHijoModalOpen(false);
    } catch (err) {
      if (axios.isAxiosError<ApiError>(err)) {
        setHijoError(err.response?.data?.mensaje ?? 'No se pudo vincular al hijo/a.');
      } else {
        setHijoError('No se pudo vincular al hijo/a.');
      }
    } finally {
      setHijoLoading(false);
    }
  }

  function setHijoEditField(field: Partial<ActualizarHijoRequest>) {
    setHijoEditForm((prev) => ({ ...prev, ...field }));
  }

  function handleHijoEditParticular(checked: boolean) {
    setHijoEditField({ particular: checked, obraSocial: checked ? '' : hijoEditForm.obraSocial, nroAfiliado: checked ? '' : hijoEditForm.nroAfiliado });
  }

  function openEditHijo(h: HijoResponse) {
    setEditingHijo(h);
    setHijoEditForm({
      particular: h.particular,
      obraSocial: h.obraSocial ?? '',
      nroAfiliado: h.nroAfiliado ?? '',
    });
    setHijoEditError(null);
  }

  function closeEditHijo() {
    setEditingHijo(null);
    setHijoEditError(null);
  }

  async function handleActualizarHijo(e: FormEvent) {
    e.preventDefault();
    setHijoEditError(null);

    if (!hijoEditForm.particular) {
      if (!hijoEditForm.obraSocial.trim()) { setHijoEditError('La obra social es obligatoria para pacientes no particulares.'); return; }
      if (!hijoEditForm.nroAfiliado.trim()) { setHijoEditError('El número de afiliado es obligatorio para pacientes no particulares.'); return; }
    }
    if (!editingHijo) return;

    setHijoEditLoading(true);
    try {
      const actualizado = await actualizarHijo(editingHijo.id, {
        particular: hijoEditForm.particular,
        obraSocial: hijoEditForm.particular ? '' : hijoEditForm.obraSocial.trim(),
        nroAfiliado: hijoEditForm.particular ? '' : hijoEditForm.nroAfiliado.trim(),
      });
      setHijos((prev) => prev.map((h) => (h.id === actualizado.id ? actualizado : h)));
      setEditingHijo(null);
    } catch (err) {
      if (axios.isAxiosError<ApiError>(err)) {
        setHijoEditError(err.response?.data?.mensaje ?? 'No se pudo actualizar la cobertura.');
      } else {
        setHijoEditError('No se pudo actualizar la cobertura.');
      }
    } finally {
      setHijoEditLoading(false);
    }
  }

  function handleParticular(checked: boolean) {
    setParticular(checked);
    if (checked) {
      setObraSocial('');
      setNroAfiliado('');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!email.trim()) { setError('El email es obligatorio.'); return; }
    if (!particular) {
      if (!obraSocial.trim()) { setError('La obra social es obligatoria para pacientes no particulares.'); return; }
      if (!nroAfiliado.trim()) { setError('El número de afiliado es obligatorio para pacientes no particulares.'); return; }
    }

    setLoading(true);
    try {
      const actualizado = await actualizarMiCuenta({
        email: email.trim(),
        particular,
        obraSocial: particular ? '' : obraSocial.trim(),
        nroAfiliado: particular ? '' : nroAfiliado.trim(),
      });
      setCuenta(actualizado);
      if (user) setUser({ ...user, email: actualizado.email });
      setSuccess(true);
    } catch (err) {
      if (axios.isAxiosError<ApiError>(err)) {
        setError(err.response?.data?.mensaje ?? 'No se pudo actualizar la cuenta.');
      } else {
        setError('No se pudo actualizar la cuenta.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate(homeForRole(user?.role))}>← Volver</button>
        <img src={logo} alt="Logo" className={styles.topbarLogo} />
        <p className={styles.topbarTitle}>Mi cuenta</p>
        <p className={styles.topbarBrand}>Laboratorio Sagrado Corazón</p>
        <UserMenu />
      </header>

      <main className={styles.content}>
        {!loadingData && isViewer && (
          <div className={styles.tabs}>
            <button
              type="button"
              className={`${styles.tabBtn} ${activeTab === 'cuenta' ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab('cuenta')}
            >
              Mi cuenta
            </button>
            <button
              type="button"
              className={`${styles.tabBtn} ${activeTab === 'hijos' ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab('hijos')}
            >
              Hijos vinculados{hijos.length > 0 ? ` (${hijos.length})` : ''}
            </button>
          </div>
        )}

        {loadingData ? (
          <div className={styles.spinnerWrapper}>
            <div className={styles.spinner} />
            <p className={styles.spinnerText}>Cargando datos...</p>
          </div>
        ) : (!isViewer || activeTab === 'cuenta') && (
        <div className={styles.card}>
          <div className={styles.avatarLarge}>{(cuenta?.nombre ?? '?').charAt(0).toUpperCase()}</div>
          <h2 className={styles.name}>{cuenta?.nombre} {cuenta?.apellido}</h2>

          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>DNI</span>
            <span className={styles.infoValue}>{cuenta?.dni}</span>
          </div>
          <div className={`${styles.infoRow} ${styles.infoRowLast}`}>
            <span className={styles.infoLabel}>Género</span>
            <span className={styles.infoValue}>{cuenta?.genero === 'M' ? 'Femenino' : 'Masculino'}</span>
          </div>

          {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
          {success && <p className={styles.successMsg}>Tus datos se guardaron correctamente.</p>}

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <TextInput
              id="email"
              label="Email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
            />

            <div className={styles.checkboxField}>
              <input
                id="particular"
                type="checkbox"
                checked={particular}
                onChange={(e) => handleParticular(e.target.checked)}
              />
              <label htmlFor="particular">Paciente particular</label>
            </div>

            {!particular && (
              <div className={styles.row2}>
                <Select
                  id="obraSocial"
                  label="Obra social"
                  value={obraSocial}
                  onChange={setObraSocial}
                  options={obrasSociales.map((o) => ({ value: o.nombre, label: o.nombre }))}
                  placeholder="Seleccioná una obra social"
                />
                <TextInput
                  id="nroAfiliado"
                  label="N° de afiliado"
                  value={nroAfiliado}
                  onChange={setNroAfiliado}
                />
              </div>
            )}

            <Button type="submit" loading={loading} loadingText="Guardando...">
              Guardar cambios
            </Button>
          </form>

          <button type="button" className={styles.changePasswordBtn} onClick={() => navigate('/change-password')}>
            Cambiar contraseña
          </button>
        </div>
        )}

        {!loadingData && isViewer && activeTab === 'hijos' && (
          <div className={`${styles.card} ${styles.hijosCard}`}>
            <div className={styles.hijosHeader}>
              <h2 className={styles.hijosTitle}>Hijos vinculados</h2>
              <button type="button" className={styles.vincularHijoBtn} onClick={openVincularHijo}>
                + Vincular hijo/a
              </button>
            </div>

            {loadingHijos ? (
              <p className={styles.hijosEmpty}>Cargando...</p>
            ) : hijos.length === 0 ? (
              <p className={styles.hijosEmpty}>Todavía no vinculaste a ningún hijo/a menor de edad.</p>
            ) : (
              <div className={styles.hijosList}>
                {hijos.map((h) => (
                  <div key={h.id} className={styles.hijoRow}>
                    <div className={styles.hijoInfo}>
                      <span className={styles.hijoNombre}>{h.apellido}, {h.nombre}</span>
                      <span className={styles.hijoDetalle}>
                        DNI {h.dni} · {calcularEdad(h.fechaNacimiento)} años · {h.particular ? 'Particular' : h.obraSocial}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={styles.editarHijoBtn}
                      onClick={() => openEditHijo(h)}
                      title="Editar cobertura"
                    >
                      <i className="bi bi-pencil-fill"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <Modal
        isOpen={hijoModalOpen}
        onClose={closeVincularHijo}
        title="Vincular hijo/a"
        width={560}
        disableClose={hijoLoading}
      >
        {hijoError && <ErrorBanner message={hijoError} onClose={() => setHijoError(null)} />}
        <form onSubmit={handleVincularHijo} noValidate>
          <div className={styles.row2}>
            <TextInput
              id="hijoNombre"
              label="Nombre"
              value={hijoForm.nombre}
              onChange={(v) => setHijoField({ nombre: v })}
            />
            <TextInput
              id="hijoApellido"
              label="Apellido"
              value={hijoForm.apellido}
              onChange={(v) => setHijoField({ apellido: v })}
            />
          </div>

          <div className={styles.row2}>
            <TextInput
              id="hijoDni"
              label="DNI"
              value={hijoForm.dni}
              onChange={(v) => setHijoField({ dni: v })}
              placeholder="Ej: 45123456"
            />
            <Select
              id="hijoSexo"
              label="Sexo"
              value={hijoForm.sexo}
              onChange={(v) => setHijoField({ sexo: v as 'M' | 'H' })}
              options={[
                { value: 'M', label: 'Femenino' },
                { value: 'H', label: 'Masculino' },
              ]}
            />
          </div>

          <TextInput
            id="hijoFechaNacimiento"
            label="Fecha de nacimiento"
            type="date"
            value={hijoForm.fechaNacimiento}
            onChange={(v) => setHijoField({ fechaNacimiento: v })}
          />

          <div className={styles.checkboxField}>
            <input
              id="hijoParticular"
              type="checkbox"
              checked={hijoForm.particular}
              onChange={(e) => handleHijoParticular(e.target.checked)}
            />
            <label htmlFor="hijoParticular">Paciente particular</label>
          </div>

          {!hijoForm.particular && (
            <div className={styles.row2}>
              <Select
                id="hijoObraSocial"
                label="Obra social"
                value={hijoForm.obraSocial}
                onChange={(v) => setHijoField({ obraSocial: v })}
                options={obrasSociales.map((o) => ({ value: o.nombre, label: o.nombre }))}
                placeholder="Seleccioná una obra social"
              />
              <TextInput
                id="hijoNroAfiliado"
                label="N° de afiliado"
                value={hijoForm.nroAfiliado}
                onChange={(v) => setHijoField({ nroAfiliado: v })}
              />
            </div>
          )}

          <div className={styles.hijoFormActions}>
            <Button type="submit" loading={hijoLoading} loadingText="Vinculando...">
              Vincular
            </Button>
            <button type="button" className={styles.cancelHijoBtn} onClick={closeVincularHijo} disabled={hijoLoading}>
              Cancelar
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={editingHijo !== null}
        onClose={closeEditHijo}
        title={`Editar cobertura de ${editingHijo?.nombre ?? ''}`}
        width={480}
        disableClose={hijoEditLoading}
      >
        {hijoEditError && <ErrorBanner message={hijoEditError} onClose={() => setHijoEditError(null)} />}
        <p className={styles.editHijoHint}>
          Solo se puede modificar si es particular, la obra social y el número de afiliado. Para cambiar otros
          datos, comunicate con el laboratorio.
        </p>
        <form onSubmit={handleActualizarHijo} noValidate>
          <div className={styles.checkboxField}>
            <input
              id="hijoEditParticular"
              type="checkbox"
              checked={hijoEditForm.particular}
              onChange={(e) => handleHijoEditParticular(e.target.checked)}
            />
            <label htmlFor="hijoEditParticular">Paciente particular</label>
          </div>

          {!hijoEditForm.particular && (
            <div className={styles.row2}>
              <Select
                id="hijoEditObraSocial"
                label="Obra social"
                value={hijoEditForm.obraSocial}
                onChange={(v) => setHijoEditField({ obraSocial: v })}
                options={obrasSociales.map((o) => ({ value: o.nombre, label: o.nombre }))}
                placeholder="Seleccioná una obra social"
              />
              <TextInput
                id="hijoEditNroAfiliado"
                label="N° de afiliado"
                value={hijoEditForm.nroAfiliado}
                onChange={(v) => setHijoEditField({ nroAfiliado: v })}
              />
            </div>
          )}

          <div className={styles.hijoFormActions}>
            <Button type="submit" loading={hijoEditLoading} loadingText="Guardando...">
              Guardar cambios
            </Button>
            <button type="button" className={styles.cancelHijoBtn} onClick={closeEditHijo} disabled={hijoEditLoading}>
              Cancelar
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
